/* eslint-disable no-nested-ternary */
import * as React from 'react';
import { Box, Button } from '@chakra-ui/react';

import { withConfirm } from '../withConfirm';
import { Input, IProps as IInputProps } from '../Input';

export type IProps = IInputProps & {
  suggestions: Array<string>;
  suggestionsLoading: boolean;
};

type InputEvent = React.FormEvent<HTMLInputElement>;

class AutocompleteInput extends React.Component<IProps, { selected: number }> {
  suggestionsListRef: React.RefObject<HTMLDivElement>;
  inputRef: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
  inputNode?: HTMLElement | null;

  static defaultProps = {
    suggestions: [],
    suggestionsLoading: false,
  };

  constructor(props: IProps) {
    super(props);
    this.state = { selected: -1 };
    this.suggestionsListRef = React.createRef();

    // handle both callback style and "modern" React refs
    if (typeof props.forwardedRef === 'function') {
      this.inputRef = element => {
        this.inputNode = element;
        (props.forwardedRef as (instance: HTMLInputElement | null) => void)(element);
      };
    } else {
      this.inputRef = props.forwardedRef || React.createRef();
    }
  }

  showSuggestions = () => {
    if (this.state.selected < 0) {
      this.setState({ selected: 0 });
    }
  };

  onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    switch (evt.key) {
      case 'ArrowUp':
        evt.preventDefault();
        this.highlight(this.state.selected <= 0 ? this.props.suggestions.length - 1 : this.state.selected - 1);
        break;
      case 'ArrowDown':
        evt.preventDefault();
        this.highlight(this.state.selected === this.props.suggestions.length - 1 ? 0 : this.state.selected + 1);
        break;
      case 'Tab':
      case 'Enter':
        if (this.state.selected >= 0) {
          evt.preventDefault();
          this.onSelect(this.props.suggestions[this.state.selected]);
        }
        break;
      case 'Escape':
        this.deselect();
        break;
      default:
        this.setState({ selected: 0 });
    }
  };

  deselect = () => {
    this.setState(state => {
      if (state.selected !== -1) {
        return { selected: -1 };
      }
      return null;
    });
  };

  onBlur = (evt: React.FormEvent<HTMLInputElement>) => {
    this.deselect();
    if (this.props.onBlur) this.props.onBlur(evt);
  };

  onFocus = (evt: React.FormEvent<HTMLInputElement>) => {
    // this.setState(state => {
    // 	if (state.selected !== 0) {
    // 		return { selected: 0 };
    // 	}
    // 	return null;
    // });
    if (this.props.onFocus) this.props.onFocus(evt);
  };

  highlight = (selected: number) => {
    this.setState({ selected });
    this.scrollSuggestionIntoView(selected);
  };

  scrollSuggestionIntoView = (index: number) => {
    const node = this.suggestionsListRef.current;
    if (node) {
      const listItem = node.children[index];
      // Check if function exists first as not supported in IE or Safari (as usual!)
      if (typeof listItem.scrollIntoView === 'function') {
        listItem.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  };

  focusInput = () => {
    let node;
    if (typeof this.inputRef === 'function') {
      node = this.inputNode;
    } else {
      node = (this.inputRef as React.RefObject<HTMLInputElement>).current;
    }
    if (node && typeof node.focus === 'function') {
      node.focus();
    }
  };

  onSelect = (value: string) => {
    const fakeInputEvent = {
      currentTarget: {
        value,
      },
    } as InputEvent;
    this.deselect();
    if (typeof this.props.onChange === 'function') this.props.onChange(fakeInputEvent);
    this.focusInput();
  };

  getAutoSuggestControl = () => (
    <>
      {this.props.suggestions.length >= 0 && this.state.selected >= 0 && (
        <Box
          aria-autocomplete="list"
          position="absolute"
          zIndex="popover"
          left="0"
          right="0"
          top="100%"
          mt={{ base: 1, lg: 2 }}
          px={2}
          py={2}
          bgColor="chakra-body-bg"
          borderRadius="lg"
          boxShadow="md"
          maxHeight="16rem"
          overflowY="auto"
          _dark={{ bgColor: 'gray.700' }}
        >
          {this.props.suggestions.length ? (
            <Box display="flex" flexDirection="column" ref={this.suggestionsListRef}>
              {(this.props.suggestions || []).map((suggestion: string, i: number) => (
                <Button
                  key={i}
                  data-selector={`suggestion-${i}`}
                  isActive={this.state.selected === i}
                  onClick={() => this.onSelect(suggestion)}
                  onMouseEnter={() => this.highlight(i)}
                  onMouseDown={(e: React.MouseEvent) => {
                    e.preventDefault();
                  }}
                  variant="ghost"
                  size="md"
                  colorScheme="gray"
                  fontWeight="regular"
                  width="100%"
                  textAlign="left"
                  justifyContent="start"
                  borderRadius="sm"
                  flexGrow="1"
                  py={2}
                  height={'auto'}
                  whiteSpace={'normal'}
                >
                  {suggestion}
                </Button>
              ))}
            </Box>
          ) : this.props.suggestionsLoading ? (
            <Box py={2} px={4} color="muted">
              Loading...
            </Box>
          ) : (
            <Box py={2} px={4} color="muted">
              No Suggestions
            </Box>
          )}
        </Box>
      )}
    </>
  );

  render() {
    return (
      <Input
        {...this.props}
        onChange={this.props.onChange}
        onKeyDown={this.onKeyDown}
        onBlur={this.onBlur}
        onFocus={this.onFocus}
        forwardedRef={this.inputRef}
        autoSuggest={true}
        autoSuggestControl={this.getAutoSuggestControl()}
      />
    );
  }
}

const AutocompleteInputWithConfirm = withConfirm(AutocompleteInput);

export { AutocompleteInput, AutocompleteInputWithConfirm };
